<template>
  <div ref="sonarSidebar" class="flat-scroll sonar-sidebar">
    <div v-if="showQuestions" class="sonar-sidebar__running">
      <SonarQuestion
        v-bind="{
          currentQuestion,
          isLabQuestion,
        }"
      />
      <SonarAnswers
        ref="answers"
        class="sonar-sidebar__answers"
        v-bind="{
          currentQuestion,
          lastQuestion,
          firstQuestion,
          showActionButtons: true,
          isLastQuestion,
        }"
      />
    </div>
    <div v-else class="sonar-sidebar__starting-lab-wrapper">
      <LabSession
        v-if="summaryWithLab"
        ref="labSession"
        :showStartLabCard="true"
        :simpleCard="true"
        v-bind="{
          parentId,
          parentType,
          labId,
          isLoading,
        }"
        @session-started="sessionStartedHandler"
      >
        <template slot="title">
          <h2 class="pta-card-session__title">
            Before you start, take a moment to read the overview carefully.
          </h2>
        </template>
        <template slot="ctaHandler">
          Get Started
        </template>
        <template slot="info">
          This assessment includes hands-on labs. Entering your region and
          keyboard layout ensures an optimal lab experience.
        </template>
      </LabSession>
      <InfoIcon
        v-if="!isSummary && !isChangingLabState && !isTransitioningStates"
        class="sonar-sidebar__starting-lab-icon"
      />
      <div
        v-if="!summaryWithLab"
        class="sonar-sidebar__starting-lab-cta-container"
      >
        <ActionLoader
          v-if="
            assessmentAttempt.hasLab &&
              (isChangingLabState || isTransitioningStates)
          "
          :name="'ball-beat'"
          :color="'#ffffff'"
          :size="1"
        />
        <div class="sonar-sidebar__starting-lab-info">
          {{ labQuestionsLabel }}
        </div>
        <button
          v-if="(isSummary || isLabRunning) && !isChangingLabState"
          :disabled="isCtaLoading"
          class="
            btn
            btn--primary
          "
          :class="ctaBtnClasses"
          @click="clickHandler()"
        >
          {{ ctaLabel }}
          <ActionLoader
            v-if="isCtaLoading"
            name="ball-beat"
            color="#000"
            class="sonar-sidebar__cta-loader"
          />
          <ArrowRightIcon v-if="ctaWithArrow" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { InfoIcon, ArrowRightIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import SonarAnswers from './SonarAnswers.vue'
import SonarQuestion from './SonarQuestion.vue'
import LabSession from '@/components/lab/LabSession.vue'

export default {
  name: 'SonarAssessmentSidebar',
  components: {
    SonarAnswers,
    SonarQuestion,
    InfoIcon,
    ArrowRightIcon,
    ActionLoader,
    LabSession,
  },
  props: {
    isSummary: {
      type: Boolean,
      required: true,
      default: false,
    },
    isLabRunning: {
      type: Boolean,
      required: true,
      default: false,
    },
    isCtaLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    isAssessmentRunning: {
      type: Boolean,
      required: true,
      default: false,
    },
    labHasError: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      isCtaShowed: true,
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      question: 'sonar/currentQuestion',
      assessmentAttempt: 'sonar/assessmentAttempt',
      assessments: 'sonar/assessments',
      canAnswerQuestions: 'sonar/canAnswerQuestions',
      isChangingLabState: 'labs/isChangingLabState',
      isStartingLab: 'labs/isStartingLab',
      isRestartingLab: 'labs/isRestartingLab',
      isSwitchingLabs: 'labs/isSwitchingLabs',
      isTransitioningStates: 'labs/isTransitioningStates',
    }),
    showQuestions() {
      return this.isAssessmentRunning && this.canAnswerQuestions
    },
    currentQuestion() {
      const question = this.question
      const dunnoOptionExists = this.question?.answers?.some(
        answer => answer.id === CONSTANTS.SONAR.ASSESSMENT.ANSWER.DONT_KNOW
      )
      if (question?.question_type === 'multiple_choice' && !dunnoOptionExists) {
        question.answers.push({
          content_html: "<p>I don't know</p>",
          id: CONSTANTS.SONAR.ASSESSMENT.ANSWER.DONT_KNOW,
        })
      }
      return question
    },
    ctaLabel() {
      return this.isSummary ? 'Get started' : 'Go to questions'
    },
    ctaBtnClasses() {
      return this.ctaWithArrow
        ? `btn--primary--with-icon btn--primary--with-icon--right btn--default-icon-color`
        : ''
    },
    ctaWithArrow() {
      return !this.isSummary
    },
    currentPosition() {
      return this.currentQuestion.position
    },
    totalQuestions() {
      return this.assessmentAttempt.questions.length
    },
    lastQuestion() {
      return this.assessmentAttempt.questions.at(-1)
    },
    firstQuestion() {
      return this.assessmentAttempt.questions[0]
    },
    isLastQuestion() {
      return this.totalQuestions === this.currentPosition
    },
    labQuestionsQty() {
      return this.assessmentAttempt.questions.filter(question => {
        return (
          question.labData?.content_id ===
          this.currentQuestion.labData?.content_id
        )
      }).length
    },
    isLabQuestion() {
      return this.currentQuestion.hasLab
    },
    labQuestionsQtyLabel() {
      const plural = this.labQuestionsQty > 1 ? 's are' : ' is'
      return `${
        this.labQuestionsQty > 1 ? this.labQuestionsQty : ''
      } question${plural}`
    },
    labQuestionsLabel() {
      if (this.isSummary)
        return 'Before you begin, take a moment to read the overview carefully.'
      if (this.isStartingLab || this.isTransitioningStates)
        return 'This lab is being built and will be ready soon!'
      if (this.isRestartingLab) return 'This lab is being restarted'
      if (this.isLabRunning) return 'Lab running!'
      const shouldStartLabLabel = !this.isSwitchingLabs
        ? 'to the lab. Start the lab to continue.'
        : 'to another lab. This lab is being built and will be ready soon!'
      return this.labHasError
        ? ' There was an error starting the lab. "Try restarting" button may fix the issue.'
        : `The following ${this.labQuestionsQtyLabel} related ${shouldStartLabLabel}`
    },
    parentType() {
      return CONSTANTS.PARENT_CONTENT_TYPES_KEYS.ASSESSMENT
    },
    parentId() {
      return this.assessmentAttempt.id
    },
    labId() {
      return this.assessmentAttempt?.questionsLabData?.firstLabQuestion
        ?.content_id
    },
    summaryWithLab() {
      return this.isSummary && this.labId
    },
  },
  watch: {
    currentQuestion() {
      this.$refs.sonarSidebar.scrollTop = 0
    },
  },
  methods: {
    clickHandler() {
      if (this.isSummary) this.startAssessment()
      if (this.isLabRunning) this.$parent.$emit('resume-assessment')
    },
    async startAssessment() {
      if (this.labId)
        this.$refs.labSession.$refs.currentLabSession.handleSession()
      this.$parent.$emit('start-assessment')
    },
    sessionStartedHandler(value) {
      if (value) {
        this.isLoading = true
        this.$parent.$emit('start-assessment')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-sidebar {
  height: 100%;
  &__wrapper {
    display: flex;
    justify-content: center;
    height: 100%;
  }
  &__running {
    width: 100%;
    max-width: 1200px;
    padding: 20px;
    margin: auto;
  }
  &__starting-lab-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
    padding: 40px;
    height: 100%;
  }
  &__starting-lab-icon {
    display: flex;
    justify-content: center;
    color: var(--tundora-gray);
    width: 80px;
    height: 80px;
  }
  &__starting-lab-info {
    display: flex;
    justify-content: center;
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
    text-align: center;
  }
  &__starting-lab-cta-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  &__start-questions-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
    height: 100%;
  }
  &__start-questions-label {
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
  }
  &__cta-loader {
    display: flex;
  }
  &__answers {
    width: 100%;
    padding: 0;
    @include tablet {
      padding: 0 0 40px 0;
    }
    .answers {
      &__selection {
        height: 65px;
        .answers__select {
          padding: unset;
          padding-left: 20px;
          align-self: center;
          p {
            margin: unset;
          }
        }
      }
    }
  }
}
</style>
